import React, { useState } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import { PencilIcon } from '@heroicons/react/outline'

function CouponCodesTable({ couponCodes, onCouponCodesChange, onEditCouponCode, facilityId }) {
  const [updatingIds, setUpdatingIds] = useState(new Set())

  async function handleToggleActive(couponCode, newValue) {
    const couponCodeId = couponCode.id
    setUpdatingIds((prev) => new Set([...prev, couponCodeId]))

    const optimisticCouponCodes = couponCodes.map((c) => (c.id === couponCodeId ? { ...c, active: newValue } : c))
    onCouponCodesChange(optimisticCouponCodes)

    try {
      await axios.put(`/api/facilities/${facilityId}/coupon_codes/${couponCodeId}`, {
        couponCode: {
          discountAttributes: {
            active: newValue
          }
        }
      })
    } catch (error) {
      const rollbackCouponCodes = couponCodes.map((c) => (c.id === couponCodeId ? { ...c, active: !newValue } : c))
      onCouponCodesChange(rollbackCouponCodes)

      const errorMessage =
        (error.response && error.response.data && error.response.data.error) || 'Failed to update coupon code'
      alert(`Error: ${errorMessage}`)
    } finally {
      setUpdatingIds((prev) => {
        const newSet = new Set(prev)
        newSet.delete(couponCodeId)
        return newSet
      })
    }
  }

  function formatAmount(couponCode) {
    const { reductionMethod, amount } = couponCode
    if (reductionMethod === 'percentage') {
      return `${amount}% off`
    } else {
      return `$${(amount / 100).toFixed(2)} off`
    }
  }

  function formatRedemptions(couponCode) {
    const { maxRedemptions, numRedemptions } = couponCode
    if (maxRedemptions) {
      return `${numRedemptions} of ${maxRedemptions}`
    }
    return numRedemptions || 0
  }

  return (
    <>
      <div className="hidden overflow-x-auto lg:block">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
              >
                Code
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
              >
                Amount
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
              >
                Active
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
              >
                Redemptions
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {couponCodes.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                  No coupon codes created yet. Click &quot;Add New&quot; to create one.
                </td>
              </tr>
            ) : (
              couponCodes.map((couponCode) => {
                const isUpdating = updatingIds.has(couponCode.id)
                const isMaxedOut = couponCode.maxRedemptions && couponCode.numRedemptions >= couponCode.maxRedemptions

                return (
                  <tr key={couponCode.id} className={isUpdating ? 'opacity-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{couponCode.code}</div>
                      {couponCode.description && <div className="text-sm text-gray-500">{couponCode.description}</div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatAmount(couponCode)}</div>
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={couponCode.active}
                          onChange={(e) => handleToggleActive(couponCode, e.target.checked)}
                          disabled={isUpdating}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                      </label>
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                          isMaxedOut ? 'text-gray-800 bg-gray-100' : 'text-purple-800 bg-purple-100'
                        }`}
                      >
                        {formatRedemptions(couponCode)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap">
                      <button
                        onClick={() => onEditCouponCode(couponCode)}
                        disabled={isUpdating}
                        className="inline-flex items-center text-purple-600 hover:text-purple-900 disabled:opacity-50"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="space-y-4 lg:hidden">
        {couponCodes.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500 bg-white rounded-lg">
            No coupon codes created yet. Click &quot;Add New&quot; to create one.
          </div>
        ) : (
          couponCodes.map((couponCode) => {
            const isUpdating = updatingIds.has(couponCode.id)
            const isMaxedOut = couponCode.maxRedemptions && couponCode.numRedemptions >= couponCode.maxRedemptions

            return (
              <div key={couponCode.id} className={`bg-white rounded-lg shadow p-4 ${isUpdating ? 'opacity-50' : ''}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-base font-medium text-gray-900">{couponCode.code}</h3>
                    <p className="text-sm text-gray-500">{formatAmount(couponCode)}</p>
                    {couponCode.description && <p className="mt-1 text-sm text-gray-500">{couponCode.description}</p>}
                  </div>
                  <button
                    onClick={() => onEditCouponCode(couponCode)}
                    disabled={isUpdating}
                    className="flex items-center justify-center flex-shrink-0 w-12 h-12 text-purple-600 rounded-lg hover:bg-purple-50 disabled:opacity-50"
                    aria-label="Edit coupon code"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">Active</label>
                    <div className="p-2 -m-2">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={couponCode.active}
                          onChange={(e) => handleToggleActive(couponCode, e.target.checked)}
                          disabled={isUpdating}
                        />
                        <div className="w-14 h-8 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-purple-600"></div>
                      </label>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">{couponCode.active ? 'ON' : 'OFF'}</p>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">Redemptions</label>
                    <span
                      className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                        isMaxedOut ? 'text-gray-800 bg-gray-100' : 'text-purple-800 bg-purple-100'
                      }`}
                    >
                      {formatRedemptions(couponCode)}
                    </span>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </>
  )
}

CouponCodesTable.propTypes = {
  couponCodes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      code: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
      reductionMethod: PropTypes.oneOf(['percentage', 'fixed']).isRequired,
      active: PropTypes.bool.isRequired,
      description: PropTypes.string,
      maxRedemptions: PropTypes.number,
      numRedemptions: PropTypes.number
    })
  ).isRequired,
  onCouponCodesChange: PropTypes.func.isRequired,
  onEditCouponCode: PropTypes.func.isRequired,
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
}

export default CouponCodesTable
